import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogContentText,
    DefaultButton,
    PrimaryButton,
    DialogActions,
} from '@bb-ui/react-library';
import { Prompt, useHistory } from 'react-router-dom';
import { useStyles } from './UnsavedChangesDialog.styles';

interface UnsavedChangesDialogProps {
    hasUnsavedChanges: boolean;
    onSaveAndLeave?: () => void;
}

export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
    hasUnsavedChanges,
    onSaveAndLeave,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const history = useHistory();
    const [leavingToPath, setLeavingToPath] = React.useState<string | null>(
        null,
    );

    const handleOnClose = () => {
        history.push(leavingToPath!); // Navigate to the intended path
    };
    const handleOnStay = () => {
        setLeavingToPath(null); // Hide the dialog and stay on the current page
    };
    const handleOnSaveAndClose = () => {
        if (onSaveAndLeave) {
            onSaveAndLeave();
        }
        setLeavingToPath(null); // Hide the dialog, but leave the exiting up to the standard save handler
    };

    return (
        <>
            <Prompt
                when={hasUnsavedChanges && !leavingToPath}
                message={location => {
                    setLeavingToPath(location.pathname);
                    return false;
                }}
            />
            <Dialog
                open={!!leavingToPath}
                maxWidth="md"
                onClose={handleOnStay}
                aria-labelledby="unsaved-changes-dialog-title"
            >
                <DialogTitle
                    onClose={handleOnStay}
                    id="unsaved-changes-dialog-title"
                    data-testid="unsaved-changes-dialog-title"
                    className={classes.title}
                >
                    {t('general.unsavedChangesDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="unsaved-changes-dialog-description"
                        data-testid="unsaved-changes-dialog-description"
                        className={classes.description}
                    >
                        {t('general.unsavedChangesDialog.description')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <DefaultButton onClick={handleOnClose}>
                        {t('general.unsavedChangesDialog.close')}
                    </DefaultButton>
                    <PrimaryButton onClick={handleOnSaveAndClose}>
                        {t('general.unsavedChangesDialog.saveAndClose')}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
